import {
  KeyboardEvent as ReactKeybardEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MentionsInput, Mention, OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { $aiHelper, setAiOpenOptionsEvent } from '@nx-frontend/shared/store/ai-helper';
import { getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  BorderPosition,
  PortalBorderType,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { AnimatedWritingDots } from './animated-writing-dots';
import { ChatbotAttachedFiles } from './chatbot-attached-files';
import { ChatbotContext, MentionsInputContext } from './chatbot-context';
import { ChatbotMentions } from './chatbot-mentions';
import {
  $aiMentions,
  $editMessage,
  $writingMessage,
  clearMentionsEvent,
  fetchMentionsEvent,
  setActiveThreadIdEvent,
} from './store';
import { ChatBotAttachedFile } from './types';
import { getMentionIcon } from './util';

interface ChatbotInputProps {
  users?: [];
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onSend?: (message: string) => void;
  onEndEditMessage?: () => void;
  isWriting: boolean;
  isCreatingPage: boolean;
  useWeb: boolean;
  onSwitchUseWeb: (value: boolean) => void;
  onAbortMessage: (id: string, text: string) => void;
  placeholder?: string;
  inputRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  fileUpload?: boolean;
  chooseFile?: () => void;
  chosenFiles?: ChatBotAttachedFile[];
  onRemoveFile?: (file: ChatBotAttachedFile) => void;
  sendDisabled?: boolean;
}

export function ChatbotInput({
  themeColors,
  themeStyle,
  onSend,
  onEndEditMessage,
  isWriting,
  isCreatingPage,
  useWeb,
  onSwitchUseWeb,
  onAbortMessage,
  chooseFile,
  chosenFiles,
  onRemoveFile,
  sendDisabled,
  placeholder = 'Start a chat...',
  fileUpload = false,
  inputRef: aInputRef,
}: ChatbotInputProps) {
  const [mentionsContainer, setMentionsContainer] = useState<HTMLDivElement | null>();
  const [inputValue, setInputValue] = useState('');
  const [
    writingMessage,
    aiMentions,
    fetchMentions,
    aiHelper,
    setAiOpenOptions,
    setActiveThreadId,
    clearMentions,
    editMessage,
  ] = useUnit([
    $writingMessage,
    $aiMentions,
    fetchMentionsEvent,
    $aiHelper,
    setAiOpenOptionsEvent,
    setActiveThreadIdEvent,
    clearMentionsEvent,
    $editMessage,
  ]);
  const mentionsCallback = useRef<(data: SuggestionDataItem[]) => void>(() => null);
  const aiContext = useContext(ChatbotContext);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const plainTextRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [plainText, setPlainText] = useState('');
  const [increasePaddingNeeded, setIncreasePaddingNeeded] = useState(false);
  const [textareaHasScrollBar, setTextareaHasScrollBar] = useState(false);

  const t = useTranslations('features');

  useEffect(() => {
    if (editMessage && editMessage.message) {
      setInputValue(editMessage.message);
      inputRef.current?.focus();
    }
  }, [editMessage]);

  const inputRefCallback = useCallback(
    (node: HTMLTextAreaElement) => {
      inputRef.current = node;
      if (aInputRef) {
        aInputRef.current = node;
      }
    },
    [aInputRef]
  );

  const hasTextInMessage = useMemo(() => {
    return writingMessage && writingMessage.message.length;
  }, [writingMessage]);

  const onInputChange: OnChangeHandlerFunc = (_, value, plainTextValue) => {
    setPlainText(plainTextValue);
    setInputValue(value);
  };

  const handleButtonClick = useCallback(() => {
    if (sendDisabled) {
      return;
    }
    const value = inputValue.trim();
    const mentionRegex = /:ai-mention\[.+?\]\{data-type=".+?" data-global-id=".+?"\}/gm;
    const isOnlyMentionsInValue = !value.replace(mentionRegex, '').length;
    if (value && !isOnlyMentionsInValue) {
      onSend?.(value || '');
      setIncreasePaddingNeeded(false);
      setPlainText('');
      setInputValue('');
    }
  }, [inputValue, onSend, sendDisabled]);

  const handleStopClick = useCallback(() => {
    if (writingMessage?.id && writingMessage?.message) {
      onAbortMessage(writingMessage.id, writingMessage.message);
    }
  }, [onAbortMessage, writingMessage?.id, writingMessage?.message]);

  const onKeyDown = useCallback(
    (event: ReactKeybardEvent<HTMLTextAreaElement> | ReactKeybardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleButtonClick();
      }
    },
    [handleButtonClick]
  );

  const onSearchMentions = useCallback(
    (query: string, cb: (data: SuggestionDataItem[]) => void): Promise<void> => {
      return new Promise(() => {
        mentionsCallback.current = cb;
        fetchMentions({
          sessionId: aiContext.sessionId,
          apiHost: aiContext.apiHost,
          workspaceId: aiContext.workspaceId,
          query,
          limit: aiMentions.showAll || query !== '' ? 10 : 3,
          type: aiMentions.showAll ?? undefined,
          portalId: aiContext.portalSettings?.id,
          onlyPortal: Boolean(aiContext.portalId),
        });
      });
    },
    [
      aiContext.apiHost,
      aiContext.portalSettings?.id,
      aiContext.sessionId,
      aiContext.workspaceId,
      aiContext.portalId,
      aiMentions.showAll,
      fetchMentions,
    ]
  );

  const endEditMessage = () => {
    setInputValue('');
    inputRef.current?.focus();
    onEndEditMessage?.();
  };

  const onSelectMention = useCallback(() => {
    clearMentions();
  }, [clearMentions]);

  useEffect(() => {
    if (!aiMentions.isFetching && aiMentions.items.length) {
      mentionsCallback.current(aiMentions.items);
    }
  }, [aiMentions]);

  useEffect(() => {
    if (isWriting && !isCreatingPage && hasTextInMessage) {
      const onDocumentKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          handleStopClick();
        }
      };

      document.addEventListener('keydown', onDocumentKeydown);
      return () => {
        document.removeEventListener('keydown', onDocumentKeydown);
      };
    }
  }, [isWriting, handleStopClick, isCreatingPage, hasTextInMessage]);

  useEffect(() => {
    if (aiHelper.openOptions?.mention) {
      const { mention, ...otherOptions } = aiHelper.openOptions;
      const { title, type, globalId, url } = mention;
      let mentionArgsString = `data-type="${type}" data-global-id="${globalId}"`;
      if (url) {
        mentionArgsString += ` data-mention-url="${url}"`;
      }
      setActiveThreadId(null);
      setInputValue(`:ai-mention[${title}]{${mentionArgsString}} `);
      setAiOpenOptions(otherOptions);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.selectionStart = inputRef.current.value.length;
        }
      }, 0);
    }
  }, [aiHelper.openOptions, setActiveThreadId, setAiOpenOptions]);

  useEffect(() => {
    if (plainTextRef.current && controlsRef.current) {
      const plainTextRect = plainTextRef.current.getBoundingClientRect();
      const controlsRect = controlsRef.current.getBoundingClientRect();
      if (plainTextRect.right >= controlsRect.left - 20 || plainText.indexOf('\n') >= 0) {
        if (!increasePaddingNeeded) {
          setIncreasePaddingNeeded(true);
        }
      } else if (plainTextRect.right < controlsRect.left - 20 && plainText.indexOf('\n') === -1) {
        if (increasePaddingNeeded) {
          setIncreasePaddingNeeded(false);
        }
      }
    }
  }, [increasePaddingNeeded, plainText]);

  useEffect(() => {
    if (scrollRef.current) {
      if (scrollRef.current.offsetHeight < scrollRef.current.scrollHeight) {
        if (!textareaHasScrollBar) {
          setTextareaHasScrollBar(true);
        }
      } else {
        if (textareaHasScrollBar) {
          setTextareaHasScrollBar(false);
        }
      }
    }
  }, [plainText, textareaHasScrollBar]);

  const attachFileButton = useCallback(() => {
    return aiContext.pageMode ? (
      <button
        className={classNames(
          'w-max h-7 mr-1 py-2 pl-2.5 pr-4 inline-flex items-center hover:bg-neutral-200',
          'rounded-full text-neutral-500',
          getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
        )}
        onClick={chooseFile}
      >
        <Icon icon={IconsMap.PAPERCLIP} className="w-4 h-4" />
        <div className="ml-1 text-sm">Add file</div>
      </button>
    ) : (
      <TippyTooltip message={'Attach a file'}>
        <button className="mr-1 hover:bg-neutral-200 rounded-full p-2 h-8 w-8" onClick={chooseFile}>
          <Icon icon={IconsMap.PAPERCLIP} className="w-4 h-4" />
        </button>
      </TippyTooltip>
    );
  }, [aiContext.pageMode, chooseFile, themeColors, themeStyle]);

  const mentionsTextArea = useCallback(() => {
    return (
      <>
        <div ref={setMentionsContainer} className="chatbot-mentions-container"></div>
        <MentionsInputContext.Provider value={{ textInputRef: inputRef }}>
          <MentionsInput
            inputRef={inputRefCallback}
            value={inputValue}
            onChange={onInputChange}
            className="chatbot-input w-full text-sm"
            placeholder={placeholder}
            singleLine={false}
            allowSuggestionsAboveCursor={true}
            forceSuggestionsAboveCursor={true}
            suggestionsPortalHost={mentionsContainer || undefined}
            onKeyDown={onKeyDown}
            style={{
              input: {
                outline: 'none',
                color: '#262626',
              },
              suggestions: {
                backgroundColor: 'transparent',
                overflow: 'hidden',
                zIndex: 10,
              },
            }}
            customSuggestionsContainer={ChatbotMentions}
          >
            <Mention
              trigger="@"
              data={aiContext.isVisitor ? [] : onSearchMentions}
              markup={`:ai-mention[__display__]{__id__}`}
              renderSuggestion={ChatbotMention}
              appendSpaceOnAdd={true}
              isLoading={aiMentions.isFetching}
              className="text-cyan-900 z-[1] relative pointer-events-none"
              displayTransform={(_, display) => `@${display}`}
              onAdd={onSelectMention}
              style={{
                textShadow: '1px 1px 1px white, 1px -1px 1px white, -1px 1px 1px white,-1px -1px 1px white',
              }}
            />
          </MentionsInput>
        </MentionsInputContext.Provider>
        <div className="absolute top-3.5 left-[15px] text-sm invisible" ref={plainTextRef}>
          {plainText}
        </div>
      </>
    );
  }, [
    aiContext.isVisitor,
    aiMentions.isFetching,
    inputRefCallback,
    inputValue,
    mentionsContainer,
    onKeyDown,
    onSearchMentions,
    onSelectMention,
    placeholder,
    plainText,
  ]);

  const scrollableTextContainer = useCallback(() => {
    return !aiContext.pageMode ? (
      <div
        className={classNames('w-full h-full max-h-[152px] overflow-y-auto py-[7px] flex flex-row items-center', {
          '!pb-1': increasePaddingNeeded,
          'px-[7px]': fileUpload,
          'px-[14px]': !fileUpload,
        })}
        ref={scrollRef}
      >
        {fileUpload && attachFileButton()}

        <div className="py-2 pr-[10px] flex-grow">{mentionsTextArea()}</div>
      </div>
    ) : (
      <div className="pt-3.5 pl-4 pr-[10px] flex-grow">{mentionsTextArea()}</div>
    );
  }, [aiContext.pageMode, attachFileButton, fileUpload, increasePaddingNeeded, mentionsTextArea]);

  const editMessageContent = editMessage ? (
    <div
      className={classNames(
        'w-full py-2 px-4 rounded-lg bg-neutral-100 gap-2',
        'flex relative items-center',
        'flex-grow min-h-0',
        'mx-auto',
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
      )}
    >
      <div>
        <Icon icon={IconsMap.PENCIL} className="w-4 h-4" />
      </div>

      <span>{editMessage.message}</span>

      <div className="ml-auto cursor-pointer" onClick={endEditMessage}>
        <Icon icon={IconsMap.CLOSE} className="w-4 h-4" />
      </div>
    </div>
  ) : (
    ''
  );

  const sendButtonAbsolute = useCallback(() => {
    return (
      <div className="flex absolute bottom-[9px] right-2.5 gap-4 items-center" ref={controlsRef}>
        {false && (
          <div className="flex items-center justify-end w-full">
            <TippyTooltip
              message={
                <div className="flex flex-col">
                  <span>AI searches the web</span>
                  <span>for content-related info</span>
                </div>
              }
              position={UiTooltipPosition.TOP}
              show={true}
              interactive={false}
            >
              <div>
                <Checkbox
                  type={CheckboxType.SWITCH}
                  checked={useWeb}
                  label={t('ai-helper.web_search')}
                  boxSize={CheckboxSize.SM}
                  className={classNames('text-sm transition-colors duration-500', {
                    'text-neutral-500': !useWeb,
                  })}
                  onChange={onSwitchUseWeb}
                />
              </div>
            </TippyTooltip>
          </div>
        )}
        <button
          className={classNames('rounded  text-white cursor-pointer w-8 h-8', {
            'bg-cyan-900 hover:bg-cyan-700 active:bg-cyan-700': !sendDisabled,
            'bg-neutral-200': sendDisabled,
          })}
          onClick={handleButtonClick}
        >
          <Icon icon={IconsMap.ARROW_UP_1} className="m-auto w-[18px]" />
        </button>
      </div>
    );
  }, [handleButtonClick, onSwitchUseWeb, sendDisabled, t, useWeb]);

  const aiWritingText = useCallback(() => {
    return (
      <span className="flex items-baseline gap-1">
        {!isCreatingPage ? (
          <span className="text-neutral-500">
            {!writingMessage?.isTextCompleted ? t('ai-helper.ai_is_writing') : t('ai-helper.generating_source_links')}
          </span>
        ) : (
          t('ai-helper.creating_page')
        )}
        {isCreatingPage ? <AnimatedWritingDots size={3} /> : null}
      </span>
    );
  }, [isCreatingPage, t, writingMessage?.isTextCompleted]);

  const stopButton = useCallback(() => {
    return (
      <div className="flex items-center">
        <span className={classNames('mr-2 text-[10px]', getTextClassNames(themeColors, PortalTextType.SURFACE_3))}>
          ESC
        </span>
        <button
          className="p-1.5 rounded bg-cyan-900 hover:bg-cyan-700 active:bg-cyan-700 text-white cursor-pointer w-8"
          onClick={handleStopClick}
        >
          <Icon icon={IconsMap.STOP_CIRCLE} className="w-5" />
        </button>
      </div>
    );
  }, [handleStopClick, themeColors]);

  return !aiContext.pageMode ? (
    <div
      className={classNames(
        'flex flex-col px-4 py-4 gap-2',
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE, { position: BorderPosition.TOP })
      )}
    >
      {!isWriting && fileUpload && <ChatbotAttachedFiles files={chosenFiles || []} onRemoveFile={onRemoveFile} />}
      {isWriting ? (
        <div
          className={classNames(
            'relative w-full rounded-lg flex items-center justify-between text-sm py-[9px] pl-4 pr-2.5 overflow-x-hidden min-h-[52px]',
            getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
          )}
        >
          {aiWritingText()}
          {!!(!isCreatingPage && hasTextInMessage) && stopButton()}
        </div>
      ) : (
        <>
          {editMessageContent}
          <div
            className={classNames(
              'relative w-full rounded-lg overflow-hidden',
              getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE),
              {
                '!pb-12': increasePaddingNeeded,
                'after:block after:absolute after:right-0 after:bottom-0 after:w-2 after:h-12 after:bg-[#eee]':
                  textareaHasScrollBar,
              }
            )}
          >
            {scrollableTextContainer()}
            {sendButtonAbsolute()}
          </div>
        </>
      )}
    </div>
  ) : (
    <div
      className={classNames(
        'flex flex-col gap-2',
        'max-h-[300px] p-4',
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE, { position: BorderPosition.TOP })
      )}
    >
      {!isWriting && fileUpload && (
        <div className="flex-shrink-0 max-w-2xl">
          <ChatbotAttachedFiles files={chosenFiles || []} onRemoveFile={onRemoveFile} />
        </div>
      )}
      {!isWriting && editMessage && <div className="max-w-2xl w-full mx-auto">{editMessageContent}</div>}
      {!isWriting ? (
        <div
          className={classNames(
            'w-full min-h-[80px] pb-2 rounded-lg',
            'flex flex-col relative',
            'flex-grow min-h-0',
            'max-w-2xl mx-auto',
            getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
          )}
        >
          <div className={classNames('w-full min-h-0 max-h-full mb-2', 'overflow-y-auto overflow-hidden grow')}>
            {scrollableTextContainer()}
            {sendButtonAbsolute()}
          </div>

          {fileUpload && <div className="ml-4">{attachFileButton()}</div>}
        </div>
      ) : (
        <div
          className={classNames(
            'w-full min-h-[80px] py-[9px] pl-4 pr-2.5',
            'flex justify-between items-center',
            'relative rounded-lg text-sm overflow-x-hidden',
            'max-w-2xl mx-auto',
            getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
          )}
        >
          {aiWritingText()}
          {!!(!isCreatingPage && hasTextInMessage) && stopButton()}
        </div>
      )}
    </div>
  );
}

function ChatbotMention(
  suggestion: SuggestionDataItem,
  search: string,
  highlightedDisplay: ReactNode,
  index: number,
  focused: boolean
) {
  const mention = suggestion as SuggestionDataItem & {
    type: string;
    logo?: string | null;
    color?: string;
  };
  return (
    <div
      className={classNames('px-4 py-1 flex items-center gap-2 max-w-full', {
        'bg-neutral-100': focused,
      })}
    >
      <div
        className={classNames(
          'flex items-center justify-center w-6 h-6 text-xs rounded-full overflow-hidden bg-neutral-100 text-neutral-500 flex-none',
          {
            'bg-neutral-100': !mention.color,
          }
        )}
      >
        {getMentionIcon(mention)}
      </div>
      <span className="whitespace-nowrap text-ellipsis overflow-hidden">{highlightedDisplay}</span>
    </div>
  );
}
